import {
    NewRoutingRuleResult,
    RoutingRuleAssignmentTargetType,
    RoutingRulePrepayment,
    RoutingRuleProductAssignment,
    RoutingRuleProductCategoryAssignment,
    RoutingRuleAccountingCategoryAssignment,
    RoutingRuleBillAggregationType,
} from '@mews-commander/data-access';
import { useTimeService } from '@mews-commander/ui';
import { useLocalizeText } from '@mews/framework';
import { NestedOptions, TreeOption } from '@mews/b2b-ui';
import { UnicodeEntity } from '@mews/enums';
import { TextKey } from '@mews-commander/enums';
import { mapId } from '@mews-commander/utils';
import {
    AdditionalServiceFormFields,
    AdditionalServiceOptionsNames,
    AdditionalServicesFormValues,
    CompaniesFormValues,
    CustomItemsFormFields,
    CustomItemsOptionsNames,
    ItemsAndConditionsFormFields,
    OptionsNames,
    ProductsFormFields,
    ProductsOptionsNames,
    RatesFormFields,
    RatesOptionsNames,
    RoutingRuleFormValues,
    ServiceFormFields,
    ServicesFormValues,
    TargetBillFormFields,
    TargetBillType,
} from '../../schemas';
import { RoutingRulesFormStepName, TimingFieldName } from '../../types';
import { defaultValues } from '../../constants';
import { getTriggerDayTimeValues } from '../../utils/timing';
import { useRoutingRuleIdContext } from '../../contexts/RoutingRuleIdContext';
import { useRoutingRulesData } from '../../queries';
import { useDefaultTriggerTime } from './steps/timing/hooks';
import { useServicesSuspenseQuery } from './steps/itemsAndConditions/queries';

export const DELETED_CATEGORY_ID = 'deletedCategory';

function getProductsType(
    products: RoutingRuleProductAssignment[],
    productCategories: RoutingRuleProductCategoryAssignment[],
    productAccountingCategories?: RoutingRuleAccountingCategoryAssignment[]
) {
    let productsType = ProductsOptionsNames.All;
    if (products.length > 0 && productCategories.length === 0) {
        productsType = ProductsOptionsNames.SelectedProducts;
    }
    if (products.length === 0 && productCategories.length > 0) {
        productsType = ProductsOptionsNames.SelectedProductCategories;
    }
    if (productAccountingCategories != null && productAccountingCategories.length > 0) {
        productsType = ProductsOptionsNames.SelectedAccountingCategories;
    }
    return productsType;
}

const getDefaultAggregationType = (
    targetType: TargetBillType,
    billAggregationType: RoutingRuleBillAggregationType,
    granularRoutingRulesConditionsExtensionEnabled: boolean
): RoutingRuleFormValues[RoutingRulesFormStepName.TargetBill][TargetBillFormFields.AggregationType] => {
    if (granularRoutingRulesConditionsExtensionEnabled || targetType === TargetBillType.CompanyBill) {
        return billAggregationType;
    }

    return defaultValues[RoutingRulesFormStepName.TargetBill][TargetBillFormFields.AggregationType];
};

export const useInitialValues = (
    granularRoutingRulesConditionsExtensionEnabled: boolean,
    routingRuleResult?: NewRoutingRuleResult,
    routingRuleId?: string
): RoutingRuleFormValues => {
    const timeService = useTimeService();
    const defaultTriggerTime = useDefaultTriggerTime();
    const {
        data: { bookableServices, additionalServices },
    } = useServicesSuspenseQuery();

    if (
        routingRuleId == null ||
        routingRuleResult == null ||
        routingRuleResult.routingRules == null ||
        routingRuleResult.routingRuleAssignments == null
    ) {
        return {
            ...defaultValues,
            [RoutingRulesFormStepName.Timing]: {
                ...defaultValues[RoutingRulesFormStepName.Timing],
                [TimingFieldName.TriggerTime]: defaultTriggerTime,
            },
        };
    }

    const {
        routingRules,
        routingRuleAssignments,
        rateAssignments,
        productAssignments,
        productCategoryAssignments,
        accountingCategoryAssignments,
    } = routingRuleResult;

    const rateAssignmentsArray = rateAssignments ?? [];
    const productAssignmentsArray = productAssignments ?? [];
    const productCategoryAssignmentsArray = productCategoryAssignments ?? [];
    const accountingCategoryAssignmentsArray = accountingCategoryAssignments ?? [];

    const routingRule = routingRules[0];

    const { triggerDay, triggerTime } = getTriggerDayTimeValues(routingRule.processingStartOffset);

    const includeCompanyDetails =
        routingRule.assignmentTargetType === RoutingRuleAssignmentTargetType.CompanyAsDetails
            ? [TargetBillFormFields.IncludeCompanyDetails]
            : [];

    const targetType =
        routingRule.assignmentTargetType === RoutingRuleAssignmentTargetType.CompanyAsOwner
            ? TargetBillType.CompanyBill
            : TargetBillType.GuestBill;

    const companies = routingRule.companiesWithRelations.reduce((acc: CompaniesFormValues, company) => {
        acc[company.companyId] = {
            name: company.companyName,
            linkAsCompany: acc[company.companyId]?.linkAsCompany || company.companyRelations.partnerCompany,
            linkAsTravelAgency: acc[company.companyId]?.linkAsTravelAgency || company.companyRelations.travelAgency,
        };

        return acc;
    }, {});

    const bookableServiceAssignments = routingRuleAssignments.filter((assignment) =>
        bookableServices.some((service) => service.id === assignment.serviceId)
    );
    const additionalServiceAssignments = routingRuleAssignments.filter((assignment) =>
        additionalServices.some((service) => service.id === assignment.serviceId)
    );

    const services = bookableServiceAssignments.reduce((acc: ServicesFormValues, assignment) => {
        const rates = rateAssignmentsArray.filter((rate) => rate.routingRuleAssignmentId === assignment.id);
        const products = productAssignmentsArray.filter((product) => product.routingRuleAssignmentId === assignment.id);
        const productCategories = productCategoryAssignmentsArray.filter(
            (productCategory) => productCategory.routingRuleAssignmentId === assignment.id
        );
        const productsType = getProductsType(products, productCategories);
        const isProductsOption =
            assignment.routedItemTypes.allProducts || products.length > 0 || productCategories.length > 0;

        acc[assignment.serviceId] = {
            [ServiceFormFields.Enabled]: true,
            [ServiceFormFields.Products]: {
                [ProductsFormFields.Type]: productsType,
                [ProductsFormFields.SelectedProducts]: products.length
                    ? products.map((product) => product.productId)
                    : undefined,
                [ProductsFormFields.SelectedProductCategories]: productCategories.length
                    ? productCategories.map((productCategory) => productCategory.productCategoryId)
                    : undefined,
            },
            [ServiceFormFields.Rates]: {
                [RatesFormFields.Type]: rates.length === 0 ? RatesOptionsNames.All : RatesOptionsNames.Selected,
                [RatesFormFields.SelectedRates]: rates.length > 0 ? rates.map((rate) => rate.rateId) : undefined,
            },
            [ServiceFormFields.Options]: [
                ...(assignment.routedItemTypes.spaceOrder ? [OptionsNames.TimeUnit] : []),
                ...(assignment.routedItemTypes.cityTax ? [OptionsNames.CityTaxes] : []),
                ...(assignment.routedItemTypes.deposits ? [OptionsNames.Deposits] : []),
                ...(assignment.routedItemTypes.additionalExpenses ? [OptionsNames.AdditionalExpenses] : []),
                ...(isProductsOption ? [OptionsNames.Products] : []),
            ],
        };

        return acc;
    }, {});

    const additionalServicesValues = additionalServiceAssignments.reduce(
        (acc: AdditionalServicesFormValues, assignment) => {
            const products = productAssignmentsArray.filter(
                (product) => product.routingRuleAssignmentId === assignment.id
            );
            const productCategories = productCategoryAssignmentsArray.filter(
                (productCategory) => productCategory.routingRuleAssignmentId === assignment.id
            );
            const productAccountingCategories = accountingCategoryAssignmentsArray.filter(
                (accountingCategory) =>
                    accountingCategory.routingRuleAssignmentId === assignment.id &&
                    accountingCategory.itemTypes.products
            );
            const productAccountingCategoryIds = productAccountingCategories.map(
                (accountingCategory) => accountingCategory.accountingCategoryId
            );
            const productsType = getProductsType(products, productCategories, productAccountingCategories);
            const isProductsOption =
                assignment.routedItemTypes.allProducts ||
                products.length > 0 ||
                productCategories.length > 0 ||
                productAccountingCategories.length > 0;

            const customItemAccountingCategories = accountingCategoryAssignmentsArray.filter(
                (accountingCategory) =>
                    accountingCategory.routingRuleAssignmentId === assignment.id &&
                    accountingCategory.itemTypes.customItems
            );
            const isCustomItemsOption =
                assignment.routedItemTypes.allCustomItems || customItemAccountingCategories.length > 0;
            const customItemAccountingCategoryIds = customItemAccountingCategories.map(
                (accountingCategory) => accountingCategory.accountingCategoryId
            );
            const accountingCategoryIds =
                productAccountingCategoryIds.length > 0
                    ? productAccountingCategoryIds
                    : customItemAccountingCategoryIds;

            acc[assignment.serviceId] = {
                [AdditionalServiceFormFields.Enabled]: true,
                [AdditionalServiceFormFields.Products]: {
                    [ProductsFormFields.Type]: productsType,
                    [ProductsFormFields.SelectedProducts]: products.length
                        ? products.map((product) => product.productId)
                        : undefined,
                    [ProductsFormFields.SelectedProductCategories]: productCategories.length
                        ? productCategories.map((productCategory) => productCategory.productCategoryId)
                        : undefined,
                    [ProductsFormFields.SelectedAccountingCategories]: accountingCategoryIds,
                },
                [AdditionalServiceFormFields.Options]: [
                    ...(isProductsOption ? [AdditionalServiceOptionsNames.Products] : []),
                    ...(isCustomItemsOption ? [AdditionalServiceOptionsNames.CustomItems] : []),
                ],
                [AdditionalServiceFormFields.CustomItems]: {
                    [CustomItemsFormFields.Type]:
                        customItemAccountingCategories.length > 0
                            ? CustomItemsOptionsNames.SelectedAccountingCategories
                            : CustomItemsOptionsNames.All,
                    [CustomItemsFormFields.SelectedAccountingCategories]: accountingCategoryIds,
                },
            };

            return acc;
        },
        {}
    );

    const hasSelectedRates = Object.values(services).some(
        (service) => service[ServiceFormFields.Rates]?.[RatesFormFields.SelectedRates]?.length
    );

    return {
        [RoutingRulesFormStepName.Timing]: {
            [TimingFieldName.Name]:
                routingRule.name || defaultValues[RoutingRulesFormStepName.Timing][TimingFieldName.Name],
            [TimingFieldName.Description]:
                routingRule.description || defaultValues[RoutingRulesFormStepName.Timing][TimingFieldName.Description],
            [TimingFieldName.TriggerTiming]: routingRule.triggerType,
            [TimingFieldName.ConsumptionDate]:
                routingRule.orderItemConsumptionPeriod ||
                defaultValues[RoutingRulesFormStepName.Timing][TimingFieldName.ConsumptionDate],
            [TimingFieldName.TriggerDay]:
                triggerDay || defaultValues[RoutingRulesFormStepName.Timing][TimingFieldName.TriggerDay],
            [TimingFieldName.TriggerTime]:
                (triggerTime && timeService.getShortString(triggerTime)) || defaultTriggerTime,
        },
        [RoutingRulesFormStepName.ItemsAndConditions]: {
            [ItemsAndConditionsFormFields.CompaniesEnabled]: routingRule.companiesWithRelations.length > 0,
            [ItemsAndConditionsFormFields.RatesEnabled]: hasSelectedRates,
            [ItemsAndConditionsFormFields.PrepaidReservationsEnabled]:
                routingRule.prepayment === RoutingRulePrepayment.Prepaid,
            [ItemsAndConditionsFormFields.Companies]: companies,
            [ItemsAndConditionsFormFields.Services]: services,
            [ItemsAndConditionsFormFields.AdditionalServices]: additionalServicesValues,
            [ItemsAndConditionsFormFields.GroupsAndReservationsEnabled]:
                // @ts-expect-error $TSFixMeError: Property 'reservationIds' does not exist on type 'NewRoutingRule'
                routingRule.reservationIds?.length > 0 || routingRule.reservationGroupIds?.length > 0,
            [ItemsAndConditionsFormFields.GroupsAndReservations]: {
                // @ts-expect-error $TSFixMeError: Property 'reservationIds' does not exist on type 'NewRoutingRule'
                reservationIds: routingRule.reservationIds ?? [],
                // @ts-expect-error $TSFixMeError: Property 'reservationGroupIds' does not exist on type 'NewRoutingRule'
                reservationGroupIds: routingRule.reservationGroupIds ?? [],
            },
            // @ts-expect-error $TSFixMeError: Property 'guestProfiles' does not exist on type 'NewRoutingRule'
            [ItemsAndConditionsFormFields.GuestProfilesEnabled]: (routingRule.guestProfiles ?? []).length > 0,
            // @ts-expect-error $TSFixMeError: Property 'guestProfiles' does not exist on type 'NewRoutingRule'
            [ItemsAndConditionsFormFields.GuestProfiles]: (routingRule.guestProfiles ?? []).length
                ? // @ts-expect-error $TSFixMeError: Property 'guestProfiles' does not exist on type 'NewRoutingRule'
                  mapId(routingRule.guestProfiles)
                : [],
        },
        [RoutingRulesFormStepName.TargetBill]: {
            [TargetBillFormFields.TargetType]: targetType,
            [TargetBillFormFields.AggregationType]: getDefaultAggregationType(
                targetType,
                routingRule.billAggregationType,
                granularRoutingRulesConditionsExtensionEnabled
            ),
            [TargetBillFormFields.IncludeCompanyDetails]: includeCompanyDetails,
            // @ts-expect-error $TSFixMeError: Property 'guestProfileTarget' does not exist on type 'NewRoutingRule'
            [TargetBillFormFields.GuestProfileTarget]: routingRule.guestProfileTarget ?? null,
        },
    };
};

export const useCreateDeletedCategory = (deletedItems: { id: string; name: string }[]): NestedOptions | null => {
    const localizeText = useLocalizeText();
    const deletedOptions = deletedItems.map((item) => ({
        label: `${item.name} ${UnicodeEntity.LeftParenthesis}${localizeText({ textKey: TextKey.Deleted })}${
            UnicodeEntity.RightParenthesis
        }`,
        id: item.id,
        value: item.id,
    }));

    if (deletedOptions.length === 0) {
        return null;
    }

    return {
        label: localizeText({ textKey: TextKey.Deleted }),
        id: DELETED_CATEGORY_ID,
        options: deletedOptions,
    };
};

export const useCreateDeletedItemsCountText = (options: TreeOption[]) => {
    const localizeText = useLocalizeText();
    const deletedCategory = options.find((category): category is NestedOptions => category.id === DELETED_CATEGORY_ID);
    const deletedItemsCount = deletedCategory?.options?.length ?? 0;
    return deletedItemsCount > 0
        ? `${UnicodeEntity.LeftParenthesis}${deletedItemsCount} ${localizeText({
              textKey: TextKey.Deleted,
          }).toLowerCase()}${UnicodeEntity.RightParenthesis}`
        : '';
};

export const useDeletedItems = (serviceId: string) => {
    const { routingRuleId } = useRoutingRuleIdContext();
    const { data } = useRoutingRulesData(routingRuleId);
    const routingRuleAssignmentId = data?.routingRuleAssignments?.find(
        (assignment) => assignment.serviceId === serviceId
    )?.id;

    const deletedRates =
        data?.rateAssignments
            ?.filter((rate) => !rate.isActive && rate.routingRuleAssignmentId === routingRuleAssignmentId)
            .map((rate) => ({
                id: rate.rateId,
                name: rate.rateName,
            })) ?? [];

    const deletedProducts =
        data?.productAssignments
            ?.filter((product) => !product.isActive && product.routingRuleAssignmentId === routingRuleAssignmentId)
            .map((product) => ({
                id: product.productId,
                name: product.productName,
            })) ?? [];

    const deletedProductCategories =
        data?.productCategoryAssignments
            ?.filter(
                (productCategory) =>
                    !productCategory.isActive && productCategory.routingRuleAssignmentId === routingRuleAssignmentId
            )
            .map((productCategory) => ({
                id: productCategory.productCategoryId,
                name: productCategory.productCategoryName,
            })) ?? [];

    const deletedAccountingCategories =
        data?.accountingCategoryAssignments
            ?.filter(
                (accountingCategory) =>
                    !accountingCategory.isActive &&
                    accountingCategory.routingRuleAssignmentId === routingRuleAssignmentId
            )
            .map((accountingCategory) => ({
                id: accountingCategory.accountingCategoryId,
                name: accountingCategory.accountingCategoryName,
            })) ?? [];

    const deletedProductsCategory = useCreateDeletedCategory(deletedProducts);
    const deletedProductCategoriesCategory = useCreateDeletedCategory(deletedProductCategories);
    const deletedRatesCategory = useCreateDeletedCategory(deletedRates);
    const deletedAccountingCategoriesCategory = useCreateDeletedCategory(deletedAccountingCategories);

    return {
        deletedRatesCategory,
        deletedProductsCategory,
        deletedProductCategoriesCategory,
        deletedAccountingCategoriesCategory,
    };
};
